import React, { useState } from "react";
import {
  Modal,
  Form,
  DatePicker,
  InputNumber,
  Input,
  Alert,
  Descriptions,
  Select,
} from "antd";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { recordDelivery } from "../../api/outsourceApi";

const RecordDeliveryModal = ({
  visible,
  outsourceJob,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const sent = outsourceJob?.sent_quantity || outsourceJob?.quantity || 0;
  const alreadyReceived = outsourceJob?.total_received || 0;
  const pendingQuantity = sent - alreadyReceived;

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await recordDelivery(outsourceJob.id, {
        received_qty: values.received_qty,
        rejected_qty: values.rejected_qty || 0,
        qc_status: values.qc_status || "pending",
        qc_notes: values.qc_notes,
        challan_no: values.challan_no,
        challan_date: values.challan_date
          ? values.challan_date.format("YYYY-MM-DD")
          : undefined,
        notes: values.notes,
      });
      toast.success("Delivery recorded successfully");
      form.resetFields();
      onSuccess();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to record delivery"
      );
    }
    setLoading(false);
  };

  return (
    <Modal
      title={`Record Delivery: ${outsourceJob?.outsource_number || ""}`}
      open={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      destroyOnClose
    >
      {outsourceJob && (
        <>
          <Descriptions
            size="small"
            column={3}
            bordered
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="Sent">
              {sent.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Already Received">
              {alreadyReceived.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Pending">
              <strong style={{ color: pendingQuantity > 0 ? "#fa8c16" : "#52c41a" }}>
                {pendingQuantity.toLocaleString()}
              </strong>
            </Descriptions.Item>
          </Descriptions>

          {pendingQuantity > 0 ? (
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                challan_date: dayjs(),
                received_qty: pendingQuantity,
                qc_status: "pending",
              }}
            >
              <Form.Item
                name="received_qty"
                label="Quantity Received (OK)"
                rules={[{ required: true, message: "Enter received quantity" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={pendingQuantity}
                />
              </Form.Item>

              <Form.Item name="rejected_qty" label="Quantity Rejected">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={pendingQuantity}
                />
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  prev.rejected_qty !== curr.rejected_qty
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue("rejected_qty") > 0 && (
                    <Form.Item
                      name="qc_notes"
                      label="Rejection / QC Notes"
                      rules={[
                        { required: true, message: "Provide rejection reason" },
                      ]}
                    >
                      <Input.TextArea rows={2} />
                    </Form.Item>
                  )
                }
              </Form.Item>

              <Form.Item name="qc_status" label="QC Status">
                <Select>
                  <Select.Option value="pending">Pending</Select.Option>
                  <Select.Option value="passed">Passed</Select.Option>
                  <Select.Option value="failed">Failed</Select.Option>
                  <Select.Option value="partial_pass">
                    Partial Pass
                  </Select.Option>
                </Select>
              </Form.Item>

              <Form.Item name="challan_no" label="Challan No.">
                <Input placeholder="CH-789" />
              </Form.Item>

              <Form.Item name="challan_date" label="Challan Date">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={2} placeholder="Delivery remarks..." />
              </Form.Item>
            </Form>
          ) : (
            <Alert
              message="All items received"
              description="This outsource job has been fully received."
              type="success"
              showIcon
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default RecordDeliveryModal;
